import React, { useState, useEffect } from 'react';
import { 
  Popover, 
  List, 
  Typography, 
  Box, 
  Button, 
  CircularProgress,
  Tabs,
  Tab,
  Alert,
  Divider,
  IconButton,
  Collapse,
  Tooltip
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import NetworkCheckIcon from '@mui/icons-material/NetworkCheck';
import { useNavigate } from 'react-router-dom';
import { NotificacaoInterface } from '../../types/NotificacaoInterface';
import { useNotificacoes } from '../../hooks/useNotificacoes';
import ItemNotificacao from './ItemNotificacao';
import { TipoNotificacao } from '../../utils/enums';

interface PainelNotificacoesProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
}

const PainelNotificacoes: React.FC<PainelNotificacoesProps> = ({
  anchorEl,
  onClose,
}) => {
  const [tabAtiva, setTabAtiva] = useState<number>(0);
  const [expandirErro, setExpandirErro] = useState<boolean>(false);
  const [verificandoConexao, setVerificandoConexao] = useState<boolean>(false);
  const [statusConexao, setStatusConexao] = useState<string | null>(null);
  const navigate = useNavigate();
  const { 
    notificacoes, 
    carregando, 
    erro, 
    carregarNotificacoes, 
    marcarComoLida,
    limparErro,
    verificarConexao
  } = useNotificacoes({
    carregarAutomaticamente: true,
    apenasNaoLidas: tabAtiva === 1,
  });

  // Efeito para recarregar notificações quando o painel é aberto
  useEffect(() => {
    if (anchorEl) {
      carregarNotificacoes(tabAtiva === 1);
    }
  }, [anchorEl, carregarNotificacoes, tabAtiva]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabAtiva(newValue);
    carregarNotificacoes(newValue === 1);
  };

  const handleMarcarComoLida = async (id: number) => {
    await marcarComoLida(id);
  };

  const handleNavegar = (notificacao: NotificacaoInterface) => {
    // Se não estiver lida, marca como lida
    if (!notificacao.lida) {
      marcarComoLida(notificacao.id);
    }

    // Navega para a página apropriada com base no tipo de notificação
    switch (notificacao.tipo as TipoNotificacao) {
      case TipoNotificacao.TICKET_CRIADO:
      case TipoNotificacao.TICKET_ATUALIZADO:
      case TipoNotificacao.TICKET_FECHADO:
      case TipoNotificacao.TICKET_REABERTO:
      case TipoNotificacao.TICKET_ATRIBUIDO:
      case TipoNotificacao.COMENTARIO_ADICIONADO:
      case TipoNotificacao.ANEXO_ADICIONADO:
        navigate(`/tickets/${notificacao.idRef}`);
        break;
      default:
        // Para outros tipos, apenas fecha o painel
        break;
    }
    
    onClose();
  };

  const handleRecarregar = () => {
    limparErro();
    setStatusConexao(null);
    carregarNotificacoes(tabAtiva === 1);
  };

  const toggleExpandirErro = () => {
    setExpandirErro(!expandirErro);
  };

  const handleVerificarConexao = async () => {
    try {
      setVerificandoConexao(true);
      const resultado = await verificarConexao();
      setStatusConexao(resultado.status);
    } catch (error) {
      setStatusConexao('erro');
    } finally {
      setVerificandoConexao(false);
    }
  };

  const aberto = Boolean(anchorEl);
  const id = aberto ? 'painel-notificacoes' : undefined;

  // Verificar se o erro está relacionado a JSON
  const isJsonError = erro?.message?.includes('JSON');

  return (
    <Popover
      id={id}
      open={aberto}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      PaperProps={{
        sx: {
          width: 360,
          maxHeight: 500,
          overflow: 'hidden',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        },
      }}
    >
      <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" component="div">
          Notificações
        </Typography>
        <Box>
          <Tooltip title="Verificar conexão com o servidor">
            <IconButton 
              size="small" 
              color="inherit" 
              onClick={handleVerificarConexao}
              disabled={verificandoConexao}
              sx={{ mr: 1 }}
            >
              <NetworkCheckIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Recarregar notificações">
            <IconButton 
              size="small" 
              color="inherit" 
              onClick={handleRecarregar}
              disabled={carregando}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      
      <Tabs
        value={tabAtiva}
        onChange={handleTabChange}
        variant="fullWidth"
        indicatorColor="primary"
        textColor="primary"
      >
        <Tab label="TODAS" />
        <Tab label="NÃO LIDAS" />
      </Tabs>
      
      <Divider />
      
      {statusConexao && (
        <Alert 
          severity={statusConexao === 'online' ? 'success' : statusConexao === 'online_com_erro' ? 'warning' : 'error'} 
          onClose={() => setStatusConexao(null)}
          sx={{ m: 1 }}
        >
          {statusConexao === 'online' 
            ? 'Conexão com o servidor estabelecida com sucesso.' 
            : statusConexao === 'online_com_erro'
            ? 'Servidor respondeu com erros. Contate o suporte.'
            : 'Servidor de notificações indisponível.'}
        </Alert>
      )}
      
      {erro && (
        <Alert 
          severity="error" 
          onClose={limparErro}
          sx={{ m: 1 }}
          action={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {isJsonError && (
                <IconButton
                  size="small"
                  onClick={toggleExpandirErro}
                  sx={{ mr: 1 }}
                >
                  {expandirErro ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              )}
              <Button 
                color="inherit" 
                size="small" 
                onClick={handleRecarregar}
                disabled={carregando}
              >
                Tentar novamente
              </Button>
            </Box>
          }
        >
          <Box>
            {erro.message || 'Erro ao carregar notificações. Tente novamente.'}
            
            {isJsonError && (
              <Collapse in={expandirErro}>
                <Box sx={{ mt: 1, p: 1, bgcolor: 'rgba(0,0,0,0.05)', borderRadius: 1, fontSize: '0.8rem' }}>
                  <Typography variant="caption" component="div" sx={{ fontWeight: 'bold' }}>
                    Detalhes técnicos:
                  </Typography>
                  <Typography variant="caption" component="div">
                    Houve um problema ao processar a resposta do servidor. Isso pode ocorrer quando:
                  </Typography>
                  <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                    <li>O servidor retornou dados em um formato inválido</li>
                    <li>A resposta não é um JSON válido</li>
                    <li>Há um problema de configuração no servidor</li>
                  </ul>
                  <Typography variant="caption" component="div">
                    Por favor, contate o suporte técnico se o problema persistir.
                  </Typography>
                  <Button 
                    size="small" 
                    variant="outlined" 
                    onClick={handleVerificarConexao}
                    disabled={verificandoConexao}
                    sx={{ mt: 1, fontSize: '0.7rem' }}
                    startIcon={<NetworkCheckIcon fontSize="small" />}
                  >
                    Verificar conexão
                  </Button>
                </Box>
              </Collapse>
            )}
          </Box>
        </Alert>
      )}
      
      <Box sx={{ overflow: 'auto', maxHeight: 300 }}>
        {carregando || verificandoConexao ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={30} />
          </Box>
        ) : notificacoes.length > 0 ? (
          <List sx={{ p: 0 }}>
            {notificacoes.map((notificacao) => (
              <ItemNotificacao
                key={notificacao.id}
                notificacao={notificacao}
                onMarcarComoLida={handleMarcarComoLida}
                onNavegar={handleNavegar}
              />
            ))}
          </List>
        ) : (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="textSecondary">
              {tabAtiva === 0
                ? 'Você não tem notificações.'
                : 'Você não tem notificações não lidas.'}
            </Typography>
          </Box>
        )}
      </Box>
      
      <Divider />
      
      <Box sx={{ 
        p: 1.5, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        backgroundColor: '#f5f5f5'
      }}>
        <Button 
          size="small" 
          variant="outlined"
          onClick={onClose}
          sx={{ 
            minWidth: '100px',
            textTransform: 'uppercase',
            fontWeight: 'bold',
            fontSize: '0.75rem'
          }}
        >
          Fechar
        </Button>
        <Button 
          size="small" 
          variant="contained" 
          color="primary"
          onClick={() => navigate('/notificacoes')}
          sx={{ 
            minWidth: '100px',
            textTransform: 'uppercase',
            fontWeight: 'bold',
            fontSize: '0.75rem'
          }}
        >
          Ver todas
        </Button>
      </Box>
    </Popover>
  );
};

export default PainelNotificacoes; 